import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
  Stack,
  Box,
  DialogContentText,
} from "@mui/material";
import { fontStyles } from "components/Common/commonStyles";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { mergeObjectArrays } from "components/Common/utilities";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import SnackBar from "components/SnackBar/SnackBar";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import PropTypes from "prop-types";
import {
  deleteOMTAttachmentByFileDocLibId,
  getOMTAttachments,
  getOMTClassDetailsFlags,
  saveOMTAttachments,
} from "services/userServices";
import { Close, Delete } from "@mui/icons-material";
import BackdropComponent from "components/Spinner/Spinner";
import { red } from "@mui/material/colors";
import { Transition } from "components/Common/Header/Header";

function ShowFileList(props) {
  const { files, onRemove } = props;
  return (
    <Grid container>
      {files?.map((file) => (
        <Grid item xs={2} key={file?.name}>
          <Chip
            label={file?.name}
            sx={{
              ...fontStyles,
              letterSpacing: 0.1,
              fontWeight: "bold",
              color: "#000",
            }}
            onDelete={() => onRemove(file)}
          />
        </Grid>
      ))}
    </Grid>
  );
}

ShowFileList.propTypes = {
  files: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const validImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

const textAlignLeft = {
  textAlign: "left",
};

export default function OMTAttachments(props) {
  const { openAttachmentDialog, row, category, onClose, editAccess } = props;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState("");
  const [attachmentDetails, setAttachmentDetails] = useState([]);
  const [traineeFlagDetails, setTraineeFlagDetails] = useState([]);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);
  const [selectedRowForDeletion, setSelectedRowForDeletion] = useState({});
  const dispatch = useDispatch();
  const { t } = useTranslation(["FileAttachment"], { useSuspense: false });

  useEffect(() => {
    if (!openAttachmentDialog) return;

    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const [attachmentsRes, flagsRes] = await Promise.allSettled([
          getOMTAttachments(
            row?.OMTClassDetailsId,
            row?.ScheduleType,
            category,
          ),
          getOMTClassDetailsFlags(row?.OMTClassDetailsId),
        ]);

        if (cancelled) return;

        if (
          attachmentsRes.status === "fulfilled" &&
          attachmentsRes.value?.status === 200
        ) {
          if (!cancelled) {
            setAttachmentDetails(attachmentsRes.value.data?.data || []);
          }
        } else {
          console.error("Attachments fetch failed:", attachmentsRes.reason);
        }

        if (flagsRes.status === "fulfilled" && flagsRes.value?.status === 200) {
          if (!cancelled) {
            setTraineeFlagDetails(flagsRes.value.data?.data || []);
          }
        } else {
          console.error("Flags fetch failed:", flagsRes.reason);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch attachment data:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    openAttachmentDialog,
    row?.OMTClassDetailsId,
    row?.ScheduleType,
    category,
    refreshCounter,
  ]);

  const showSnack = (message, severity) => {
    dispatch(
      SnackBarStoreAction.updateSnack({ open: true, message, severity }),
    );
  };

  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      const isValidType = validImageTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_BYTES;

      if (!isValidType) {
        showSnack(`File "${file.name}" is not a valid image type.`, "warning");
      }

      if (!isValidSize) {
        showSnack(
          `File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`,
          "warning",
        );
      }

      return isValidType && isValidSize;
    });

    setFiles(mergeObjectArrays(files, validFiles, "name"));
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      const response = await saveOMTAttachments(
        files,
        category,
        row?.ClassRosterId,
        row?.OracleId,
        row?.OMTClassDetailsId,
        row?.ScheduleType,
        0,
        comments,
        row?.NoRosterIdClassName,
      );
      setLoading(false);

      if (response?.statusCode === 200) {
        setRefreshCounter((prev) => prev + 1);
        setFiles([]);
        setComments("");
        showSnack(response?.message, "success");
      } else {
        showSnack("Internal error occurred", "error");
      }
    } catch (error) {
      setLoading(false);
      console.error("Upload failed:", error);
      showSnack("Upload failed due to a network or server error.", "error");
    }
  };

  const onFileRemove = (file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
  };

  const handleDeleteRosterAttachment = async (rowData) => {
    try {
      setLoading(true);
      const response = await deleteOMTAttachmentByFileDocLibId(
        rowData?.FileDocLibId,
      );
      setLoading(false);

      if (response?.status === 200) {
        showSnack("Deleted successfully", "success");

        setAttachmentDetails(
          attachmentDetails.filter(
            (row) => row.FileDocLibId !== rowData?.FileDocLibId,
          ),
        );
        setRefreshCounter((prev) => prev + 1);
      } else {
        showSnack("Failed to delete attachment", "error");
      }
    } catch (error) {
      setLoading(false);
      console.error("Delete failed:", error);
      showSnack("An error occurred while deleting the attachment", "error");
    } finally {
      setIsDeleteConfirmationDialogOpen(false);
      setSelectedRowForDeletion({});
    }
  };

  const attachmentColumns = [
    {
      field: "FileDocLibId",
      header: "Delete",
      align: "left",
      bodyStyle: textAlignLeft,
      body: (rowData) => (
        <IconButton
          onClick={() => {
            setIsDeleteConfirmationDialogOpen(true);
            setSelectedRowForDeletion(rowData);
          }}
          sx={{ color: red[500], fontSize: 12, cursor: "pointer" }}
        >
          <Delete />
        </IconButton>
      ),
    },
    {
      field: "OMTScheduleStatusType",
      header: "ScheduleType",
      sortable: false,
      align: "left",
      bodyStyle: textAlignLeft,
    },
    {
      field: "FileName",
      header: "File Name",
      sortable: true,
      align: "left",
      bodyStyle: textAlignLeft,
      body: ({ FilePathUrl, FileName }) => (
        <Link
          href={FilePathUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={FileName}
          sx={{
            color: "#3299FF",
            textDecoration: "none",
            display: "inline-block",
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {FileName}
        </Link>
      ),
    },
    {
      field: "Comments",
      header: "Comments",
      sortable: false,
      align: "left",
      bodyStyle: textAlignLeft,
    },
    {
      field: "ModifiedBy",
      header: "Modified By",
      sortable: true,
      align: "left",
      bodyStyle: textAlignLeft,
    },
    {
      field: "ModifiedDate",
      header: "Modified Date",
      sortable: true,
      align: "left",
      bodyStyle: textAlignLeft,
      body: ({ ModifiedDate }) =>
        ModifiedDate ? new Date(ModifiedDate).toLocaleString() : "NA",
    },
  ];

  const traineeFlagDetailColumns = [
    ...[
      "ScheduleType",
      "Attendance",
      "Hardware",
      "AD Credentials",
      "Network",
      "OperatingSystem",
      "Application",
    ].map((field) => {
      const dataKey = field === "AD Credentials" ? "Credentials" : field; // Map display name to data key
      return {
        field: dataKey,
        header: field,
        sortable: false,
        align: "left",
        bodyStyle: textAlignLeft,
        body: (rowData) => {
          const items = rowData?.[dataKey]?.split("|") || [];
          return (
            <Stack spacing={0.25}>
              {items.map((item, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box
                    component={"span"}
                    sx={{ fontSize: "1rem", lineHeight: 1 }}
                  >
                    •
                  </Box>{" "}
                  {item}
                </Typography>
              ))}
            </Stack>
          );
        },
      };
    }),
    {
      field: "TaskType",
      header: "TaskType",
      sortable: false,
      align: "left",
      bodyStyle: textAlignLeft,
    },
  ];

  return (
    <>
      {" "}
      {loading ? <BackdropComponent /> : null}
      <Dialog
        open={openAttachmentDialog}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Grid container>
            <Grid item xs={11.8}>
              {" "}
              <TextLabel
                text={"Upload Attachments"}
                fontColor={"#000"}
                fontSize={16}
              />
            </Grid>
            <Grid item xs={0.2} sx={{ pt: 0.2 }}>
              <Close sx={{ color: "#3F5765" }} onClick={onClose} />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers={true}>
          <Grid container rowGap={1}>
            <Grid item xs={0.5}>
              <TextLabel text={"Name :"} subHeading></TextLabel>
            </Grid>
            <Grid item xs={11.5}>
              <TextLabel text={row?.EmployeeName} description></TextLabel>
            </Grid>
            <Grid item xs={12}>
              <PrimeReactDataGrid
                dataKey="FileDocLibID"
                size="small"
                removableSort
                resizableColumns
                showGridlines
                paginator={traineeFlagDetails.length > 5}
                rows={5}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate={t(
                  "pagination_template.showing_first_to_last_of_totalrecords_record_s_",
                )}
                columnResizeMode="expand"
                value={traineeFlagDetails}
                columns={traineeFlagDetailColumns}
                metaKeySelection={false}
                enableGlobalFilter={false}
                enableStickyHeader={true}
                enableScrollable={false}
                sortMode="multiple"
                emptyMessage={t("empty_message.no_records_found_")}
              ></PrimeReactDataGrid>
            </Grid>
            <Grid item xs={2}>
              <CustomButton
                variant="contained"
                sx={{ color: "#FFF" }}
                component="label"
              >
                Select Files
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileChange}
                />
              </CustomButton>
            </Grid>

            <Grid item container xs={10}>
              {files?.length > 0 && (
                <ShowFileList files={files} onRemove={onFileRemove} />
              )}
            </Grid>
            <Grid item xs={12}>
              {attachmentDetails?.length > 0 && (
                <PrimeReactDataGrid
                  dataKey="FileDocLibID"
                  size="small"
                  removableSort
                  resizableColumns
                  showGridlines
                  paginator={attachmentDetails.length > 5}
                  rows={5}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                  currentPageReportTemplate={t(
                    "pagination_template.showing_first_to_last_of_totalrecords_record_s_",
                  )}
                  columnResizeMode="expand"
                  value={attachmentDetails}
                  columns={attachmentColumns}
                  metaKeySelection={false}
                  enableGlobalFilter={false}
                  enableStickyHeader={true}
                  enableScrollable={false}
                  sortMode="multiple"
                  emptyMessage={t("empty_message.no_records_found_")}
                ></PrimeReactDataGrid>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Comments"
                multiline
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                fullWidth
                InputProps={{
                  sx: {
                    ...fontStyles,
                  },
                }}
                helperText={`${comments ? comments.length : 0}/${100}`}
                inputProps={{ maxLength: 100 }}
                placeholder="Max 100 Characters"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton
            variant="contained"
            disabled={files.length === 0 || !editAccess}
            onClick={handleUpload}
            sx={{ color: "#FFF" }}
          >
            Upload
          </CustomButton>
          <CustomButton
            onClick={onClose}
            variant="outlined"
            disabled={!editAccess}
          >
            Cancel
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDeleteConfirmationDialogOpen}
        onClose={() => setIsDeleteConfirmationDialogOpen(false)}
        TransitionComponent={Transition}
      >
        {loading ? <BackdropComponent /> : null}
        <DialogTitle>{"Delete Attachment"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container>
              <Grid item xs={12}>
                {" "}
                <b>{selectedRowForDeletion?.FileName}</b>
              </Grid>
              <Grid item xs={12}>
                {" "}
                Are you sure you want to delete ?
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton
            variant="contained"
            onClick={() => handleDeleteRosterAttachment(selectedRowForDeletion)}
            sx={{ color: "#FFF" }}
          >
            Delete
          </CustomButton>

          <CustomButton
            variant="outlined"
            onClick={() => setIsDeleteConfirmationDialogOpen(false)}
          >
            Cancel
          </CustomButton>
        </DialogActions>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}

OMTAttachments.propTypes = {
  openAttachmentDialog: PropTypes.bool.isRequired,
  row: PropTypes.object.isRequired,
  category: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  editAccess: PropTypes.bool,
};
